
import React, { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Lock, Mail, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { apiCall } from "@/utils/api";

const Profile: React.FC = () => {
    const { user, token, logout } = useAuth(); // Need to refresh user data?
    // actually useAuth usually keeps user in state, but doesn't have a reloadUser function exposed typically.
    // For now we trust the local state or fetch new state?
    // Ideally we should update the context state.

    // Let's assume we just update backend and maybe minimal local state if AuthContext doesn't expose refresh.
    // However, if name/email changes, sidebar should reflect it.
    // AuthContext usually hydrates from /auth/user on mount.

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user) {
            setName(user.name || "");
            setEmail(user.email || "");
            setUsername(user.username || "");
        }
    }, [user]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password && password !== confirmPassword) {
            toast.error("Les mots de passe ne correspondent pas");
            return;
        }

        setSaving(true);
        try {
            const payload: any = {
                name,
                email,
            };
            if (password) {
                payload.password = password;
                payload.password_confirmation = confirmPassword;
            }

            // Note: Route in backend test was /auth/profile (no api prefix).
            // But frontend config usually appends /api.
            // If backend stripped it, then frontend calling /api/auth/profile ends up as /auth/profile in backend.
            // So we use standard /auth/profile endpoint here.

            const response = await apiCall<{ message: string, user: any }>(token, '/auth/profile', 'PUT', payload);

            toast.success("Profil mis à jour avec succès");
            setPassword("");
            setConfirmPassword("");

            // Reload page to refresh context? or better:
            // window.location.reload(); 
            // Or just rely on next fetch.

        } catch (error: any) {
            console.error("Profile update error:", error);
            const msg = error.message || "Erreur lors de la mise à jour";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mx-auto px-4 py-8 max-w-2xl animate-in fade-in duration-700">
            <Card className="border-slate-200 shadow-sm rounded-3xl overflow-hidden bg-white">
                <CardHeader className="bg-slate-50/50 border-b border-slate-100 p-8">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-white rounded-2xl shadow-sm border border-slate-100 text-primary">
                            <User className="h-6 w-6" />
                        </div>
                        <div>
                            <CardTitle className="text-xl font-bold text-slate-900">Mon Profil</CardTitle>
                            <CardDescription className="text-slate-500 font-medium">Gérez vos informations personnelles et votre sécurité.</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="username">Nom d'utilisateur</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                    id="username"
                                    value={username}
                                    disabled
                                    className="pl-10 bg-slate-50 text-slate-500 cursor-not-allowed border-slate-200"
                                />
                            </div>
                            <p className="text-[10px] text-slate-400 font-medium">Le nom d'utilisateur ne peut pas être modifié.</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label htmlFor="name">Nom complet</Label>
                                <Input
                                    id="name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    className="bg-white border-slate-200 focus:ring-primary/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="pl-10 bg-white border-slate-200 focus:ring-primary/10"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-slate-100 pt-6 mt-6">
                            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <Lock className="h-4 w-4 text-primary" />
                                Changer le mot de passe
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="password">Nouveau mot de passe</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Laisser vide pour ne pas changer"
                                        className="bg-white border-slate-200"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Confirmer le nouveau mot de passe"
                                        required={!!password}
                                        className="bg-white border-slate-200"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 flex justify-end">
                            <Button
                                type="submit"
                                disabled={saving}
                                className="font-bold shadow-lg shadow-primary/20 transition-all active:scale-95 w-full sm:w-auto"
                            >
                                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                <Save className="mr-2 h-4 w-4" />
                                Enregistrer les modifications
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default Profile;
